<template>
  <div class="auth-shell auth-shell--centered">
    <div class="auth-card">
      <div class="auth-card__logo">
        <img :src="logoPath" alt="logo" />
      </div>
      <div class="auth-card__heading">
        <h3>{{ $t("pages.login.description") }}</h3>
        <h5>{{ $t("pages.login.init_session") }}</h5>
      </div>

      <form @submit.prevent="auth" class="auth-card__panel">
        <div class="alert alert-danger" role="alert" v-if="error">
          {{ error }}
        </div>

        <name-field :form="form" autocomplete="username" name="name" id="name" />

        <password-name-field
          :form="form"
          autocomplete="current-password"
          name="password"
          id="password"
          type="password"
        />

        <div class="text-center mt-2 mb-2">
          <router-link :to="{ name: 'recover-password' }">{{
            $t("pages.login.forgot_password")
          }}</router-link>
        </div>

        <div class="text-center">
          <vue-button
            :title="$t('pages.login.sign_in')"
            :loading="isLoading"
            class="btn btn-primary btn-block p-3 text-uppercase"
            type="submit"
          ></vue-button>
        </div>
      </form>

      <div class="auth-card__footer-link">
        <a href @click.prevent="changeCompany">{{ $t("pages.appConfiguration.changeCompany") }}</a>
      </div>
      <div class="auth-card__copyright">
        {{ copyright }}
      </div>
      <div class="auth-card__stores">
        <google-play-badge class="mr-3" />
        <apple-store-badge />
      </div>
    </div>
  </div>
</template>

<script>
import api from "../../api/user";

import { mapActions, mapGetters } from "vuex";
import { VueButton, Form } from "@/components/form";
import { RequiredRule } from "@/components/form/data/rules";
import { NameField, PasswordNameField } from "@/pages/auth/fields";
import * as Sentry from "@sentry/vue";

import { AppleStoreBadge, GooglePlayBadge } from "@/components";

export default {
  layout: "BasicLayout",
  middleware: "auth",
  components: {
    VueButton,
    NameField,
    PasswordNameField,
    AppleStoreBadge,
    GooglePlayBadge,
  },
  data() {
    return {
      error: null,
      form: new Form({
        data: {
          name: null,
          password: null,
        },
      }),
      isLoading: false,
    };
  },
  computed: {
    ...mapGetters("app", ["appVersion", "copyright"]),
    ...mapGetters("auth", ["logoPath"]),
    fieldsRules() {
      return [
        new RequiredRule({
          name: "name",
          errorMessage: this.$t("components.fields.rules.required"),
        }),
        new RequiredRule({
          name: "password",
          errorMessage: this.$t("components.fields.rules.required"),
        }),
      ];
    },
  },
  methods: {
    ...mapActions("auth", ["saveAuth", "saveData", "removeAppConfiguration"]),
    clear() {
      this.form.clear();
    },
    async auth() {
      this.form.setFieldsRules(this.fieldsRules);

      if (this.form.validate()) {
        this.isLoading = true;
        this.error = "";
        const data = this.form.data();

        Sentry.setContext("authData", {
          name: data.name,
          pass: data.password,
        });

        api
          .auth(data)
          .then(async (response) => {
            this.saveAuth(response.data);

            const { data } = await api.whoAmI();
            this.saveData(data);

            await this.$router.push({ name: "dashboard" }).catch((navigationError) => {
              if (navigationError.name !== "NavigationDuplicated") {
                throw navigationError;
              }
            });
          })
          .catch((error) => {
            if (error.message.toLowerCase() === "network error") {
              this.error = this.$t("components.messages.network_error");
            } else if (error.response) {
              if (error.response.status === 404) {
                this.error = this.$t("auth.user_not_found");
                this.clear();
              } else if (error.response.status === 400) {
                console.error(error.response.data);
                this.error = this.$t(error.response.data);
              } else {
                //unsigned error
                Sentry.captureException(error);
                this.error = this.$t("components.messages.error");
              }
            } else {
              //unsigned error
              Sentry.captureException(error);
              this.error = this.$t("components.messages.error");
            }
          })
          .then(() => {
            this.isLoading = false;
          });
      }
    },
    async changeCompany() {
      this.removeAppConfiguration();
      await this.$router.push({ name: "app-configuration" });
    },
  },
};
</script>
